/**
 * Global Fetcher Reusable Function
 */

import { getPreviewMode } from '@selectors/others';
import { utils as snackbarUtils } from '@stores/snackbar';
import errorHandler from './fetchErrorHandler';
import getHeaders from './getHeaders';
import fetchWithoutErrorHandling from './fetchWithoutErrorHandling';
import getUrl from './getUrl';
import { HOST_TYPE } from './host';

export const errorHandlerWithDispatch = async ({
  error,
  dispatch,
  getState,
  endpoint,
  options,
  headers
}) => {
  if (dispatch && getState) {
    errorHandler({
      error,
      action: { type: '' },
      dispatch,
      getState,
      endpoint,
      options,
      source: 'api-helper-fetcher',
      headers
    });
    if (error?.response?.status !== 401) {
      // SWR works differently from a reducer, hence in case an unauthorized event is not thrown, show a snackbar since we don't have an action to forward to the store as a failure or a success.
      const errorMessage = error?.response?.json
        ? (await error.response.json())?.message
        : '';
      snackbarUtils.open({
        message: errorMessage || 'Something went wrong. Please try again.',
        type: 'error'
      });
    }
    return null;
  }
};

/*
  You can extend this function to add more processing for the API Data to build custom fetcher functions.
  For example: fetch(endpoint).then(data => { ... Your Processing });
*/

const fetch = async (endpoint, options = {}) => {
  const {
    dispatch,
    getState,
    useAuthToken = true,
    useSearchToken = false,
    useStarGateToken = false,
    headers = {},
    useOriginalURL = false,
    isFormData = false,
    useCamelCase = false,
    hasTextResponse = false,
    hasAppClient = false,
    hostType = HOST_TYPE.DEFAULT,
    params = {},
    pathVars = {},
    requestType = 'GET',
    body = null,
    onError = null,
    onSuccess = null
  } = options || {};

  const nextPreviewMode = getPreviewMode(getState());
  const url = useOriginalURL
    ? endpoint
    : getUrl({ endpoint, hostType, nextPreviewMode, params, pathVars });

  const apiCallHeaders = getHeaders({
    url,
    getState,
    headersOptions: {
      useAuthToken,
      useSearchToken,
      isFormData,
      useStarGateToken
    },
    hasAppClient,
    headers
  });

  let resPromise = fetchWithoutErrorHandling(url, {
    ...options,
    headers: apiCallHeaders,
    hasTextResponse,
    useCamelCase,
    requestType,
    body
  }).catch((error) => {
    errorHandlerWithDispatch({
      error,
      dispatch,
      getState,
      endpoint: url,
      options,
      headers: apiCallHeaders
    });
    if (typeof onError === 'function') onError(error);
    return { error };
  });

  if (typeof onSuccess === 'function')
    resPromise = resPromise.then((res) => {
      onSuccess(res);
      return res;
    });

  return resPromise;
};

export default fetch;
